<script setup lang="ts">
const props = defineProps<{
    title: string,
    desc: string,
    items: Array<{
        title: string,
        img: string,
        icon: string,
        groups: Array<{
            title: string,
            desc: string
        }>
    }>
}>();
const activeItem = ref<typeof props.items[number]>();
const intervalTime = 10;
const interval = useInterval(intervalTime * 1000, {
    controls: true,
    callback: () => nextItem(),
});

const activeIndex = computed(() => 
    props.items.findIndex(e => e.title === activeItem.value?.title)
);

function activeTab(index: number) {
    activeItem.value = props.items[index];
};

function handleOnClickTab(index:number) {
    if (activeIndex.value === index) return;

    interval.reset();
    interval.pause();
    activeTab(index);
    interval.resume();
};

function nextItem() {
    const index = (activeIndex.value + 1) % props.items.length;
    activeTab(index);
};


onMounted(() => {
    if (!props.items) return;

    activeItem.value = props.items[0];
});
</script>

<template>
    <SectionsBase id="solutions" class="b bg-linear-to-tr from-primary-50 via-primary-50/30 to-transparent">
        <div class="container">
            <ChipsDivider>راهکارها</ChipsDivider>
            <h2>{{ props.title }}</h2>
            <p class="text-body1-semibold text-gray-400 text-center mt-6">{{ props.desc }}</p>
            <DevicesMobile>
                <div class="flex flex-col gap-3 mt-6">
                    <div 
                        v-for="item in props.items"
                        :key="item.title"
                        class="w-full flex flex-col gap-4 px-4 py-3"
                    >
                        <div class="w-full flex items-center gap-2">
                            <Icon :name="item.icon" size="24" />
                            <h3>{{ item.title }}</h3>
                        </div>
                        <NuxtImg
                            width="300"
                            :src="item.img"
                            :alt="item.title"
                            class="w-full"
                            loading="lazy"
                        />
                        <div 
                            v-for="group in item.groups"
                            :key="group.title"
                            class="w-full bg-white rounded-xl shadow-gray-l p-3"
                            data-aos="zoom-in" 
                            data-aos-duration="1000"
                        >
                            <span class="text-center block text-body2-bold">{{ group.title }}</span>
                            <p class="mt-3 text-caption text-gray-400 text-center">{{ group.desc }}</p>
                        </div>
                    </div>
                </div>
            </DevicesMobile>

            <DevicesDesktop>
                <div class="flex items-center gap-12 mb-18 mt-5">
                    <div class="w-125">
                        <div
                            v-for="item, index in props.items"
                            :key="item.title"
                            class="w-full px-4 py-6 flex items-center gap-2 border-b border-gray-300 cursor-pointer select-none relative"
                            :class="activeItem?.title === item.title ? '' : 'text-gray-400'"
                            @click="handleOnClickTab(index)"
                        >
                            <svg 
                                v-if="index === activeIndex" 
                                class="absolute h-0.5 bg-primary-600 right-0 left-0 -bottom-px anim"
                            ></svg>
                            <Icon :name="item.icon" size="24" />
                            <h3 class="h4">{{ item.title }}</h3>                       
                        </div>
                    </div>
                    <div class="w-full min-h-110 flex-center relative">
                        <div 
                            v-for="_, index in 3"
                            :key="index"
                            class="small-card" 
                            :class="`mode-${ activeIndex }`"
                        >
                            <span class="text-center block text-body2-bold">
                                {{ activeItem?.groups[index]?.title }}
                            </span>
                            <p class="mt-3 text-body2 text-gray-400 text-center">
                                {{ activeItem?.groups[index]?.desc }}
                            </p>
                        </div>
                        <Transition name="fade" mode="out-in">
                            <img 
                                width="585"
                                :src="activeItem?.img" 
                                :alt="activeItem?.title"
                                :key="activeItem?.img"
                                class="w-100 drop-shadow-gray-300/60 drop-shadow-2xl"
                                loading="lazy"
                            >
                        </Transition>
                    </div>
                </div>
            </DevicesDesktop>
        </div>
    </SectionsBase>    
</template>

<style scoped>
@reference "~/assets/css/main.css";

.small-card {
    @apply
    w-75 bg-white rounded-xl shadow-gray-m p-4 absolute transition-transform duration-300 z-1
}
.small-card:nth-child(1) {
    &.mode-0 {
        transform: translate(250px, 190px);
    }
    &.mode-1 {
        transform: translate(260px, 140px);
    }
    &.mode-2 {
        transform: translate(260px, 150px);
    }
    &.mode-3 {
        transform: translate(230px, 160px);
    }
}
.small-card:nth-child(2) {
    &.mode-0 {
        transform: translate(-220px, 210px);
    }
    &.mode-1 {
        transform: translate(-260px, 170px);
    }
    &.mode-2 {
        transform: translate(-300px, 50px);
    }
    &.mode-3 {
        transform: translate(-250px, 150px);
    }
}
.small-card:nth-child(3) {
    &.mode-0 {
        transform: translate(-270px, -150px);
    }
    &.mode-1 {
        transform: translate(-300px, -50px);
    }
    &.mode-2 {
        transform: translate(-150px, -165px);
    }
    &.mode-3 {
        transform: translate(-230px, -150px);
    }
}

.anim {
    animation: width-timer v-bind('intervalTime + "s"') linear;
}
@keyframes width-timer {
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
}
</style>